import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react";
import { ReactNode, useMemo } from "react";
import CouponCodeItem from "./Common/CouponCodeItem";
import { useCoupons } from "../utils/hooks/useCoupons";
import {
  Grid,
  Text,
  Spinner,
  VStack,
  Callout,
} from "@wootingkb/component-library";
import { ReplaceableKey } from "../lib/helper/stringReplacer";
import { ReplaceableURL } from "./Common/RegionawareLink";

const COUPON_GO_TO_ALL_PRODUCTS = msg({
  id: "Coupon_go_to_all_products",
  message: "Go to all products",
});

const COUPON_THANK_YOU_GIVE_BACK = msg({
  id: "Coupon_thank_you_give_back",
  message:
    "Thank you for supporting us! Here's a little something to give back.",
});

const COUPON_TAKE_CONTROL_EXPIRES = msg({
  id: "Coupon_take_control_expires",
  message: "30. November 2023",
});

type CouponContent = {
  title: ReactNode;
  image: string;
  imageMobile: string;
  text: ReactNode;
  expires: ReactNode;
  product: ReplaceableURL;
  productLabel?: MessageDescriptor;
  isDisabled?: boolean;
};

type I18nFn = ReturnType<typeof useLingui>["_"];

function getCouponContent(
  i18n: I18nFn
): Record<string, CouponContent | undefined> {
  return {
    TAKE_CONTROL_FOUNDER: {
      title: (
        <Trans id="Coupon_take_control_title">
          🎉 $/€20,- Off the Take Control keycaps
        </Trans>
      ),
      image:
        "https://cdn.shopify.com/s/files/1/0012/8407/9705/files/store-image.png?v=1683728982",
      imageMobile:
        "https://cdn.shopify.com/s/files/1/0013/1383/3071/files/mobile.png?v=1683730034",
      text: (
        <Text>
          <Trans id="Coupon_take_control_text">
            We promised to make the Take Control keycaps during the Wooting 60HE
            campaign, now it&apos;s here, you&apos;re a founder, you get the
            heaviest discount. Enjoy!
          </Trans>
        </Text>
      ),
      expires: i18n(COUPON_TAKE_CONTROL_EXPIRES),
      product: `${ReplaceableKey.WootingStoreURL}/product/wooting-take-control-5-sided-dye-sub-pbt-keycaps`,
    },
    LOYALTY_ALUMAZE60: {
      title: (
        <Trans id="Coupon_alumaze60_title">🎉 15% Off the Alumaze60</Trans>
      ),
      image:
        "https://cdn.shopify.com/s/files/1/0013/1383/3071/files/shopify1.jpg?v=1692707841",
      imageMobile:
        "https://cdn.shopify.com/s/files/1/0013/1383/3071/files/white-front.jpg?v=1692718369",
      text: <Text>{i18n(COUPON_THANK_YOU_GIVE_BACK)}</Text>,
      expires: undefined,
      product: `${ReplaceableKey.WootingStoreURL}/product/alumaze60`,
    },
    LOYALTY_DETACHABLE_CABLE: {
      title: (
        <Trans id="Coupon_detachable_cable_title">
          🎉 10% Off the Detachable Cable Kit
        </Trans>
      ),
      image:
        "https://cdn.shopify.com/s/files/1/0013/1383/3071/files/cyber.jpg?v=1692709419",
      imageMobile:
        "https://cdn.shopify.com/s/files/1/0013/1383/3071/files/cable-cyber-main.jpg?v=1692718369",
      text: <Text>{i18n(COUPON_THANK_YOU_GIVE_BACK)}</Text>,
      expires: undefined,
      product: `${ReplaceableKey.WootingStoreURL}/product/detachable-cable`,
    },
    UPGRADE_ORDER: {
      title: (
        <Trans id="Coupon_upgrade_order_title">
          Add extra items to your order
        </Trans>
      ),
      image:
        "https://wooting-web-files.ams3.cdn.digitaloceanspaces.com/public/gray.jpg",
      imageMobile:
        "https://wooting-web-files.ams3.cdn.digitaloceanspaces.com/public/HIER%20EN%20NU%20KLAAR%20.jpg",
      text: (
        <VStack>
          <Callout
            status="warning"
            heading={
              <Trans id="Coupon_upgrade_order_warning">
                We&apos;re working on improving this upgrade system. Please use
                the standard Hub upgrade or contact us. Existing upgrades will
                be processed.
              </Trans>
            }
          />
          <Text>
            <Trans id="Coupon_upgrade_order_text">
              You have an open pre-order with a keyboard or keypad, but want to
              add items to it. You can use this discount code with a new order
              to merge it with your existing order. This will give you free
              shipping and alerts us of your upgrade.
            </Trans>
          </Text>
        </VStack>
      ),
      expires: undefined,
      product: `${ReplaceableKey.WootingStoreURL}/our-products`,
      productLabel: COUPON_GO_TO_ALL_PRODUCTS,
      isDisabled: true,
    },
    LOYALTY_OLD_TIMER: {
      title: <Trans id="Coupon_old_timer_title">🎉 $/€20,- Off the 80HE</Trans>,
      image:
        "https://cdn.shopify.com/s/files/1/0013/1383/3071/files/tony2.png?v=1710416319",
      imageMobile:
        "https://cdn.shopify.com/s/files/1/0013/1383/3071/files/tony.png?v=1710416320",
      text: (
        <Text>
          <Trans id="Coupon_old_timer_text">
            Thank you for supporting us through our keyboard journey,
            here&apos;s a small discount to give back.
          </Trans>
        </Text>
      ),
      expires: undefined,
      product: `${ReplaceableKey.WootingStoreURL}/wooting-80he`,
    },
    LOYALTY_SWITCHES: {
      title: (
        <Trans id="Coupon_switches_title">
          🎉 20% Off any Lekker Switch Pack
        </Trans>
      ),
      image:
        "https://cdn.shopify.com/s/files/1/0759/4357/5874/files/l60pack.jpg?v=1709831682",
      imageMobile:
        "https://cdn.shopify.com/s/files/1/0759/4357/5874/files/l60pack.jpg?v=1709831682",
      text: (
        <Text>
          <Trans id="Coupon_switches_text">
            Thanks for supporting us by having a Wooting Hall Effect Keyboard!
            Here&apos;s a discount to get the newest version of our switches for
            the best typing experience!
          </Trans>
        </Text>
      ),
      expires: undefined,
      product: `${ReplaceableKey.WootingStoreURL}/our-products?category=Switches`,
    },
    LOYALTY_OPTIMUM_CASE: {
      title: (
        <Trans id="Coupon_optimum_case_title">
          🎉 15% off the Optimum case
        </Trans>
      ),
      image:
        "https://cdn.shopify.com/s/files/1/0013/1383/3071/files/product_sq_optimum_case.png?v=1731420897",
      imageMobile:
        "https://cdn.shopify.com/s/files/1/0013/1383/3071/files/optimum-case-1.jpg?v=1730735149",
      text: <Text>{i18n(COUPON_THANK_YOU_GIVE_BACK)}</Text>,
      expires: undefined,
      product: `${ReplaceableKey.WootingStoreURL}/project-optimum`,
    },
    LOYALTY_OPTIMUM_KEYCAPS: {
      title: (
        <Trans id="Coupon_optimum_keycaps_title">
          🎉 15% off the Optimum keycaps
        </Trans>
      ),
      image:
        "https://cdn.shopify.com/s/files/1/0013/1383/3071/files/product_sq_optimum_keycaps.png?v=1731420896",
      imageMobile:
        "https://cdn.shopify.com/s/files/1/0013/1383/3071/files/optimum-keycaps-1.jpg?v=1730735205",
      text: <Text>{i18n(COUPON_THANK_YOU_GIVE_BACK)}</Text>,
      expires: undefined,
      product: `${ReplaceableKey.WootingStoreURL}/project-optimum`,
    },
  };
}

export default function CouponHistory() {
  const { coupons, isLoading } = useCoupons();
  const { _: i18n } = useLingui();
  const couponContent = useMemo(() => getCouponContent(i18n), [i18n]);

  return (
    <>
      {isLoading ? (
        <VStack alignment="centered" className="h-full">
          <Spinner />
        </VStack>
      ) : coupons.length === 0 ? (
        <VStack alignment="centered" className="h-full">
          <Text>
            <Trans id="Coupon_no_rewards_available">No rewards available</Trans>
          </Text>
        </VStack>
      ) : (
        <Grid className="grid-cols-1 gap-4 md:grid-cols-2">
          {coupons.map((coupon) => {
            const content = couponContent[coupon.campaign];
            if (!content) return null;

            return (
              <CouponCodeItem
                key={coupon.code}
                coupon={coupon}
                content={content}
              />
            );
          })}
        </Grid>
      )}
    </>
  );
}
